import type { Beat } from "../scenario/types";
import { t } from "../lib/uiStrings";
import "./replay-banner.css";

/** Replays that only show the outcome (attacker consoles, cinematics): there is
 *  no risky option to click, so the banner says "seeing" instead of "click". */
const VIEW_KINDS = new Set(["mfitclouddash", "mitmreveal", "osintrecon", "exfil", "leaksite", "ransomnote", "newscast", "newsexplainer"]);

/**
 * Shared banner for every "What would have happened" replay (store `whatIf`).
 * Shown on the replayed screen itself so learners know it is fine to click the
 * risky option there. Only on `forced` beats (or a device in `replay` mode),
 * never on a normal beat. Purely visual: pointer-events are off, so it can
 * never block the control the learner has to press.
 */
export function ReplayBanner({
  whatIf,
  beat,
  deviceKind,
  deviceReplay,
}: {
  whatIf: string | null;
  beat: Beat | null | undefined;
  deviceKind?: string;
  deviceReplay?: boolean;
}) {
  if (!whatIf || !beat) return null;
  if (!beat.forced && !deviceReplay) return null;
  if (beat.type === "teach" || beat.type === "quiz" || beat.type === "debrief" || beat.type === "intro") return null;
  const view = beat.type !== "device" && beat.type !== "voice_call" ? true : VIEW_KINDS.has(deviceKind ?? "");
  return (
    <div className="cf-replay-banner" role="status" aria-live="polite" data-variant={view ? "view" : "choice"}>
      <span className="cf-replay-banner__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18">
          <path d="M12 5V2L7 6l5 4V7a6 6 0 1 1-6 6H4a8 8 0 1 0 8-8z" fill="currentColor" />
        </svg>
      </span>
      <span className="cf-replay-banner__copy">
        <strong className="cf-replay-banner__title">{t("replay.title")}</strong>
        <span className="cf-replay-banner__body">{t(view ? "replay.bodyView" : "replay.body")}</span>
      </span>
    </div>
  );
}
